import { Link } from 'react-router-dom';
import { Code, Zap, Shield, Users, BarChart3, Globe, ChevronRight, Book, Terminal, Lightbulb } from 'lucide-react';

const DeveloperPortal = () => {
  const quickStartSteps = [
    {
      step: 1,
      title: "Get Your API Key",
      description: "Register for a developer account and generate your API key in the developer dashboard.",
      code: `# Register at: https://wayja.me/developers/register
# After approval, generate your API key in the dashboard`
    },
    {
      step: 2,
      title: "Fetch Market Data",
      description: "Use your API key to access real-time market data and place trades.",
      code: `curl -H "Authorization: Bearer YOUR_API_KEY" \\
     https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/market-data/market-sample`
    },
    {
      step: 3,
      title: "Place Your First Trade",
      description: "Integrate betting functionality into your application. Requires HMAC signing.",
      code: `# Generate HMAC signature first
TIMESTAMP=$(date +%s000)
NONCE=$(uuidgen)
BODY='{"option_id": 456, "amount": 100, "user_token": "wayja_token"}'
SIGNATURE=$(echo -n "POST\\n/functions/v1/place-bet\\n$TIMESTAMP\\n$NONCE\\n$BODY" | \\
  openssl dgst -sha256 -hmac "YOUR_SIGNING_SECRET" -binary | \\
  base64 | tr '+/' '-_' | tr -d '=')

curl -X POST \\
     -H "Authorization: Bearer YOUR_API_KEY" \\
     -H "Content-Type: application/json" \\
     -H "X-Request-Timestamp: $TIMESTAMP" \\
     -H "X-Request-Nonce: $NONCE" \\
     -H "X-Request-Signature: $SIGNATURE" \\
     -d "$BODY" \\
     https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/place-bet`
    }
  ];

  const apiFeatures = [
    {
      icon: <BarChart3 className="w-8 h-8 text-wayja-teal" />,
      title: "Real-Time Market Data",
      description: "Access live market data, odds, and trading volumes with sub-second updates."
    },
    {
      icon: <Zap className="w-8 h-8 text-wayja-coral" />,
      title: "Instant Betting",
      description: "Place trades programmatically with automatic odds calculation and wallet management."
    },
    {
      icon: <Shield className="w-8 h-8 text-wayja-teal" />,
      title: "Secure Authentication",
      description: "Enterprise-grade security with JWT tokens and API key management."
    },
    {
      icon: <Globe className="w-8 h-8 text-wayja-cyan" />,
      title: "Global Markets",
      description: "Access markets across sports, politics, crypto, and entertainment categories."
    }
  ];

  const useCases = [
    {
      title: "Trading Applications",
      description: "Build automated trading bots and algorithmic strategies for prediction markets.",
      icon: <BarChart3 className="w-6 h-6 text-wayja-teal" />
    },
    {
      title: "News & Media Platforms",
      description: "Integrate betting widgets into articles and content to boost engagement.",
      icon: <Globe className="w-6 h-6 text-wayja-cyan" />
    },
    {
      title: "Data Analytics",
      description: "Access market data for research, analysis, and market intelligence.",
      icon: <Book className="w-6 h-6 text-wayja-slate" />
    },
    {
      title: "Social Platforms",
      description: "Add prediction market functionality to social media and community features.",
      icon: <Users className="w-6 h-6 text-wayja-coral" />
    }
  ];

  return (
    <div className="min-h-screen bg-wayja-cream">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-wayja-light-teal/30 via-white to-wayja-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-wayja-black mb-6">
              Build with <span className="text-wayja-teal">Wayja</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Integrate prediction market functionality into your applications.
              Access real-time market data, place trades programmatically, and build the next generation of trading platforms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/developers/quickstart"
                className="bg-wayja-teal hover:bg-wayja-dark-teal text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                Quick Start Guide
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/developers/dashboard"
                className="border-2 border-wayja-slate hover:border-wayja-teal text-wayja-dark-slate px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                Developer Dashboard
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/developers/api-reference"
                className="border-2 border-wayja-slate hover:border-wayja-teal text-wayja-dark-slate px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                <Book className="w-5 h-5 mr-2" />
                API Reference
              </Link>
              <div className="border-2 border-gray-300 text-gray-400 px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center justify-center cursor-not-allowed opacity-60 relative">
                <Code className="w-5 h-5 mr-2" />
                Widget SDK
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                  Coming Soon
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* API Features */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Powerful APIs for Every Use Case
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From simple market data access to complex trading applications, our APIs provide everything you need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {apiFeatures.map((feature, index) => (
              <div key={index} className="bg-wayja-cream p-6 rounded-xl hover:shadow-lg transition-shadow border border-wayja-gray/50">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-wayja-black mb-2">{feature.title}</h3>
                <p className="text-wayja-dark-slate">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <div className="py-20 bg-wayja-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Follow these simple steps to integrate Wayja into your application.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {quickStartSteps.map((step, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="bg-wayja-teal text-white w-8 h-8 rounded-full flex items-center justify-center font-semibold mr-3">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-wayja-black">{step.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{step.description}</p>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
                  <pre>{step.code}</pre>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/developers/quickstart"
              className="bg-wayja-teal hover:bg-wayja-dark-teal text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center"
            >
              Start Quick Start Guide
              <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Can You Build?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Developers worldwide are using Wayja APIs to create innovative trading and betting applications.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-wayja-cream p-6 rounded-xl hover:shadow-md transition-shadow border border-wayja-gray/50">
                <div className="mb-4">{useCase.icon}</div>
                <h3 className="text-lg font-semibold text-wayja-black mb-2">{useCase.title}</h3>
                <p className="text-wayja-dark-slate text-sm">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Developer Resources */}
      <div className="py-20 bg-wayja-dark-slate text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Developer Resources
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Everything you need to build, test, and deploy your Wayja integration.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">

          <div className="text-center">
              <div className="bg-wayja-cyan w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Developer Dashboard</h3>
              <p className="text-wayja-gray mb-4">Join our developer ecosystem and monetize your integrations.</p>
              <Link to="/developers/dashboard" className="text-wayja-light-teal hover:text-wayja-teal font-medium">
                Learn More →
              </Link>
            </div>
            <div className="text-center">
              <div className="bg-wayja-teal w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Terminal className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">API Reference</h3>
              <p className="text-wayja-gray mb-4">Complete documentation for all endpoints, parameters, and responses.</p>
              <Link to="/developers/api-reference" className="text-wayja-light-teal hover:text-wayja-teal font-medium">
                View API Docs →
              </Link>
            </div>


            <div className="text-center">
              <div className="bg-wayja-cyan w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Code className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Code Examples</h3>
              <p className="text-wayja-gray mb-4">Ready-to-use code samples in multiple programming languages.</p>
              <Link to="/developers/examples" className="text-wayja-light-teal hover:text-wayja-teal font-medium">
                View Code Examples →
              </Link>
            </div>

            <div className="text-center opacity-60">
              <div className="bg-gray-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Code className="w-8 h-8" />
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="text-xl font-semibold">Widget SDK</h3>
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                  Coming Soon
                </span>
              </div>
              <p className="text-wayja-gray mb-4">Embed prediction markets with our lightweight JavaScript widget.</p>
              <span className="text-gray-400 font-medium cursor-not-allowed">
                View Widget Docs →
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-wayja-teal to-wayja-dark-teal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Building?
          </h2>
          <p className="text-xl text-wayja-light-teal mb-8">
            Join thousands of developers creating the future of prediction markets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/developers/quickstart"
              className="bg-white text-wayja-teal hover:bg-wayja-cream px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              <Lightbulb className="w-5 h-5 mr-2" />
              Start Building
            </Link>
            <Link
              to="/contact-support"
              className="border-2 border-white text-white hover:bg-white hover:text-wayja-teal px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperPortal;
